import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {Subscription} from 'rxjs';

import {FisherLicense} from './fisher-license.model';
import {LicenseSearchParams} from './license-search-params.model';
import {publicLicenseSearchParams} from './public-license-search.constants';
import {PublicLicenseSearchService} from './public-license-search.service';
import {validateDealerId} from '@app/shared/validators/dealer-id.validator';
import {AppError} from '@app/domain/model/error';
import {FormControlUtils} from '@app/util/form-group-utils';

@Component({
  selector: 'app-search',
  templateUrl: './public-license-search.component.html',
  styleUrls: ['./public-license-search.component.scss']
})
export class PublicLicenseSearchComponent implements OnInit, OnDestroy {

  readonly numberOfDisplayedRows = 10;
  fisherLicenses: FisherLicense[];
  searchForm: FormGroup;
  isTablePaginationHidden: boolean;
  isSearchPerformed: boolean;
  activeColumn: number;

  private queryParamsSubscription: Subscription;

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly formBuilder: FormBuilder,
    private readonly publicLicenseSearchService: PublicLicenseSearchService
  ) {
  }

  ngOnInit() {
    this.initForm();
    this.getPublicSearchQueryParams();
  }

  ngOnDestroy() {
    this.queryParamsSubscription.unsubscribe();
  }

  onSubmit() {
    FormControlUtils.markAllTouched(this.searchForm);

    this.router.navigate([], {
      queryParams: this.addParamsToUrl(this.searchForm.value)
    });
  }

  onResetClick() {
    this.searchForm.reset();
  }

  onNewSearchClick() {
    this.searchForm.reset();
    this.isSearchPerformed = false;
    this.activeColumn = -1;

    this.deleteQueryParamsFromUrl();
  }

  private initForm() {
    const formFields = {};

    formFields[publicLicenseSearchParams.licNo] = ['', validateDealerId];
    formFields[publicLicenseSearchParams.firstName] = [''];
    formFields[publicLicenseSearchParams.lastName] = [''];
    formFields[publicLicenseSearchParams.vesselName] = [''];

    this.searchForm = this.formBuilder.group(formFields, {validator: this.oneParamRequired});
  }

  oneParamRequired(c: FormGroup) {
    const regExp: RegExp = /^\d{2}\/\d{4}$/i;
    const licNo = <string>c.get('licNo').value,
      firstName = <string>c.get('firstName').value,
      lastName = <string>c.get('lastName').value,
      vesselName = <string>c.get('vesselName').value;

    if(!licNo && !firstName && !lastName && !vesselName){
      return {
        oneParamRequired: {
          valid: false
        }
      };
    }
    return null;
  }


  private performSearch(searchParams: LicenseSearchParams) {

    this.publicLicenseSearchService.performSearch(searchParams).subscribe((result: FisherLicense[]) => {
      if ( result.length > 0 ) {
        if (result.length < this.numberOfDisplayedRows) {
          this.isTablePaginationHidden = true;
        } else {
          this.isTablePaginationHidden = false;
        }

        this.fisherLicenses = result;
        this.isSearchPerformed = true;
      } else {
        this.isSearchPerformed = false;
        this.searchForm.setErrors({ 'noResultsFound': true });
      }
    },
    (error: AppError) => {
      this.deleteQueryParamsFromUrl();
      if(error.error[0].code === 'tooManyResults'){
        this.searchForm.setErrors({ 'tooManyResults': true });
      } else {
        throw error;
      }
    });
  }

  private addParamsToUrl(searchParams: LicenseSearchParams) {
    const params = {};

    Object.values(publicLicenseSearchParams).map((param) => {
      if (!searchParams[param]) {
        return;
      }

      params[param] = searchParams[param].toString().trim();
    });

    return params;
  }

  private getPublicSearchQueryParams() {
    this.queryParamsSubscription = this.activatedRoute.queryParams
      .subscribe((params: LicenseSearchParams) => {
        if (!Object.keys(params).length) {
          return;
        }

        this.performSearch(params);
      });
  }

  private deleteQueryParamsFromUrl() {
    this.router.navigate([], {
      queryParams: {}
    });
  }

}
